import React, { useState, useEffect, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import { CiSearch } from "react-icons/ci";
import { FaFileDownload } from "react-icons/fa";
import ModalDetailTrip from "components/MonitoringTrips/Modal/DetailTrip";
import { getHistory, getOperators } from "actions/Monitoring";
import DataTable from "components/DataTable";
import MonitoringColumns from "utils/columns/MonitoringTrips";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getCurrentDate, notify, status } from "utils";
import { crearExcel } from "utils/Excel";
import { columnsMonitorinExcel } from "utils/columns/columnsExcel";
import { format } from "date-fns";
import Loader from "components/UI/Loader";

export default function MonitorViajes() {
  const { currentProject } = useSelector((state) => state.auth);
  const [t] = useTranslation("monitoring");
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState([]);
  const [detail, setDetail] = useState(null);
  const [operators, setOperators] = useState([]);
  const [filter, setFilter] = useState({
    idOperator: null,
    dateInit: "",
    dateEnd: "",
  });

  const getParams = useCallback(() => {
    const params = Object.entries(filter)
      .filter(
        ([_, value]) =>
          value !== null && value !== undefined && value !== "" && value !== "0"
      )
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
      )
      .join("&");

    return params ? `?${params}` : "";
  }, [filter]);

  const validateDates = (dateInit, dateEnd) => {
    if (new Date(dateInit) > new Date(dateEnd)) {
      notify("warning", t("alerts.error_invalid_date_range"), 4000);
      return false;
    }
    return true;
  };

  const getData = async (idProject, params = getParams()) => {
    if (!validateDates(filter.dateInit, filter.dateEnd)) return;
    const response = await getHistory(idProject, params);
    setData(response?.tracks ?? []);
  };

  useEffect(() => {
    if (currentProject && currentProject?.id) {
      async function loadData() {
        try {
          const [report, operatorsList] = await Promise.all([
            getHistory(currentProject.id, ""),
            getOperators(currentProject.id),
          ]);
          setData(report?.tracks ?? []);
          setOperators(operatorsList);
        } finally {
          setIsLoading(false);
        }
      }
      loadData();
    }
  }, [currentProject]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilter({
      ...filter,
      [name]: value,
    });
  };

  const handleSearch = () => {
    if (currentProject && currentProject?.id) {
      getData(currentProject.id); // Llama a getData cuando el usuario haga clic en el botón
    }
  };

  const onViewDetails = (item) => {
    setIsOpen(true);
    setDetail(item);
  };

  const getDate = (dateStr) => {
    if (!dateStr) return "";
    if (dateStr === "0001-01-01T00:00:00") return "";

    return format(new Date(dateStr), "dd/MM/yyyy HH:mm:ss");
  };

  const handleCreationExcel = async () => {
    try {
      if (data.length <= 0) {
        notify("warning", "No hay datos", 600);
        return;
      }

      const fileName = `Monitor${getCurrentDate()}.xlsx`;
      const sheetName = "Reporte";

      const formattedData = data.map((info) => ({
        ...info,
        newStatus: status(t)[info.status] ?? "Sin estatus",
        operatorName: info.operator
          ? `${info.operator.user.name ?? ""} ${
              info.operator.user.lastName ?? ""
            } ${info.operator.user.secondLastName ?? ""}`
          : "",
        tipoUnidad: info.truck ? info.truck.name : "",
        plateNumber: info.truck ? info.truck.plateNumber : "",
        destinationNumber: `${
          info.destination?.length
            ? info.destination.filter((item) => item.type === "destino").length
            : 0
        } Destinos`,
        origen:
          info.destination.length > 0
            ? info.destination.find((item) => item.type === "origen")?.shPt
            : "",
        dateRealInit: getDate(info.createdAt),
        realStartTrip: getDate(info.startTrip),
        realEndTrip: getDate(info.endTrip),
      }));

      await crearExcel(
        fileName,
        sheetName,
        formattedData,
        columnsMonitorinExcel(t)
      );
      console.log("Archivo Excel creado exitosamente");
    } catch (error) {
      console.error("Error al crear el archivo Excel:", error);
    }
  };

  const table = useReactTable({
    data,
    columns: MonitoringColumns(onViewDetails, t),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="bg-[#f4f3fb] min-h-screen p-6">
      <div className="bg-black flex flex-row justify-between text-white px-6 py-4 rounded-t-xl">
        <h1 className="text-lg font-bold">{t("monitoring.travelMonitor")}</h1>
        <div className="flex flex-wrap justify-end items-center gap-4">
          <div className="flex flex-col">
            <label className="text-sm font-medium">
              {t("monitoring.operator")}:
            </label>
            <select
              className="border text-black border-gray-300 rounded px-2 py-1 text-sm min-w-[200px]"
              name="idOperator"
              value={filter.idOperator}
              onChange={handleChange}
              disabled={isLoading}
            >
              <option value={0}>Selecciona una opción</option>
              {operators.map((item, index) => (
                <option value={item.id} key={index}>
                  {" "}
                  {item.user.name} {item.user.lastName}{" "}
                  {item.user.secondLastName}{" "}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-medium">
              {t("monitoring.start_date")}:
            </label>
            <input
              className="text-sm text-black border border-gray-300 rounded px-2 py-1"
              name="dateInit"
              type="date"
              value={filter.dateInit}
              onChange={handleChange}
            />
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-medium">
              {t("monitoring.end_date")}:
            </label>
            <input
              className="text-sm text-black border border-gray-300 rounded px-2 py-1"
              name="dateEnd"
              type="date"
              value={filter.dateEnd}
              onChange={handleChange}
            />
          </div>
          <button
            onClick={() => handleSearch()}
            className="h-8 w-8 bg-white border border-gray-300 py-1 rounded-full hover:bg-gray-100 shadow text-black self-end"
          >
            <CiSearch className="text-2xl self-center justify-self-center" />
          </button>
          <button
            onClick={() => handleCreationExcel()}
            className="h-8 w-8 bg-white border border-gray-300 py-1 rounded-full hover:bg-gray-100 shadow text-black self-end"
          >
            <FaFileDownload className="text-2xl self-center justify-self-center" />
          </button>
        </div>
      </div>
      {isLoading ? (
        <Loader />
      ) : (
        <div className="lg:h-[75vh] pt-2">
          <DataTable table={table} w="100%" />
        </div>
      )}

      {detail && (
        <ModalDetailTrip
          data={detail}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          getRouteFromApi={true}
          showExtraInfo={true}
        />
      )}
    </div>
  );
}
